import React, { useState, useEffect, useRef } from 'react'
import adminRequest from 'utils/adminRequest'
import styled from 'styled-components'
import {
  Card,
  Button,
  Input,
  Select,
  Badge,
  Modal,
  message,
  Upload,
  Form,
  Table,
  Tag,
  Dropdown,
  Menu,
  Checkbox,
  Row,
  Col,
} from 'antd'
import {
  UploadOutlined,
  TeamOutlined,
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import Drawer from 'components/Drawer'
import { FONT } from 'theme/font'
import * as XLSX from 'xlsx'
import {
  countryCodeOptions,
  DEFAULT_COUNTRY_CODE,
  formatMobile,
  nationalNumberLength,
  normalizeCountryCodeCell,
  parseCandidateMobile,
  splitFullMobile,
} from 'utils/candidateMobile'
import { getEligibilityCriteria } from '../../../actions'
import AssessmentEndDateBanner from './AssessmentEndDateBanner'
import { buildEndDateNotice } from './assessmentEndDateNotice'

const DrawerContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fafbfc;
`

const ContentWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;

  /* Held back until the end date is settled. The inert attribute is what
     actually takes the subtree out of reach — pointer events, focus and the
     a11y tree — while the blur and fade say so visually. This CSS is the
     fallback for browsers without inert; on its own it would still let a
     keyboard user tab in. */
  &[data-gated='true'] {
    filter: blur(3px);
    opacity: 0.45;
    pointer-events: none;
    user-select: none;
  }
`

const ScreenContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 24px;
  overflow-y: auto;
  transition: all 0.3s ease;
  transform: translateX(${props => props.offset}%);
  opacity: ${props => (props.isVisible ? 1 : 0)};
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  z-index: ${props => (props.isVisible ? 1 : 0)};
  background: #fafbfc;
`

const MainOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px; /* ✅ Reduced from 24px */
  margin-bottom: 24px; /* ✅ Reduced from 32px */
`

const MethodCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid ${props => (props.selected ? '#444CE7' : '#e8e9ea')};
  border-radius: 8px; /* ✅ Reduced from 12px */
  box-shadow: ${props =>
    props.selected
      ? '0 4px 16px rgba(24, 144, 255, 0.12)'
      : '0 2px 6px rgba(0, 0, 0, 0.04)'};
  background: white;

  &:hover {
    border-color: #444ce7;
    box-shadow: 0 4px 16px rgba(24, 144, 255, 0.12);
    transform: translateY(-1px); /* ✅ Reduced from -2px */
  }

  .ant-card-body {
    padding: 12px; /* ✅ Reduced from 16px */
    text-align: center;
    min-height: 80px; /* ✅ Reduced from 120px */
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`

const IconWrapper = styled.div`
  font-size: 28px; /* ✅ Reduced from 36px */
  color: ${props => (props.selected ? '#444CE7' : '#8c8c8c')};
  margin-bottom: 8px; /* ✅ Reduced from 12px */
  transition: all 0.3s ease;
`

const MethodTitle = styled.h4`
  margin: 0; /* ✅ Removed bottom margin */
  font-size: ${FONT.F_MD}; /* ✅ Reduced from F_LG */
  font-weight: 600;
  color: ${props => (props.selected ? '#444CE7' : '#2c3e50')};
`

const MethodDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: ${FONT.F_SM};
  line-height: 1.5;
`

const SummarySection = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e8e9ea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`

const CandidateItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  margin: 8px 0;
  background: white;
  border-radius: 12px;
  border-left: 4px solid ${props => props.sourceColor};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
`

const FooterButtons = styled.div`
  padding: 20px 24px;
  border-top: 1px solid #e8e9ea;
  display: flex;
  justify-content: space-between;
  background: white;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05);
`

const UploadArea = styled.div`
  background: ${props => (props.isDragging ? '#f0f8ff' : '#fafbfc')};
  border: 2px dashed ${props => (props.isDragging ? '#444CE7' : '#d9d9d9')};
  border-radius: 12px;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 20px 0;

  &:hover {
    // background: #f0f8ff;
    border-color:#444CE7;
  }
`

const ManualEntryCard = styled(Card)`
  background: white;
  border-radius: 12px;
  border: 1px solid #e8e9ea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin: 20px 0;

  .ant-card-body {
    padding: 24px;
  }
`

const FormRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;

  .ant-input,
  .ant-select {
    flex: 1;
    border-radius: 8px;
    border: 1px solid #e8e9ea;

    &:focus,
    &:hover {
      border-color: #444CE7;
    }
  }
`

const SectionTitle = styled.h3`
  font-size: ${FONT.F_XL};
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`

const SubSectionTitle = styled.h4`
  font-size: ${FONT.F_LG};
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  background: white;
  border-radius: 12px;
  border: 1px solid #e8e9ea;
`

const RequiredFieldsSection = styled.div`
  // background: linear-gradient(135deg, #f0f8ff 0%, #e6f4ff 100%);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #d6e4ff;
  margin-bottom: 24px;
`

const InfoBanner = styled.div`
  margin-bottom: 20px;
  padding: 16px 20px;
  // background: linear-gradient(135deg, #f0f8ff 0%, #e6f4ff 100%);
  border-radius: 12px;
  border: 1px solid #d6e4ff;
  // border-left: 4px solid #444CE7;
`

const ListCard = styled(Card)`
  margin-bottom: 12px;
  border: 2px solid ${props => (props.selected ? '#444CE7' : '#e8e9ea')};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  position: relative;

  &:hover {
    border-color: #444CE7;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
  }

  .ant-card-body {
    padding: 20px;
  }
`

const ListHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 8px;
`

const ListTitle = styled.div`
  font-weight: 600;
  font-size: ${FONT.F_MD};
  color: #1f2937;
  flex: 1;
`

const ListMeta = styled.div`
  font-size: ${FONT.F_SM};
  color: #6b7280;
  margin-bottom: 12px;
  line-height: 1.4;
`

const ListFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const UploadedBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 20px;
  padding: 4px 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #52c41a;
  font-size: ${FONT.F_XS};
  font-weight: 600;
`

const StyledSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    border-radius: 8px !important;
    min-height: 40px !important;
    border: 1px solid #e8e9ea !important;
    padding: 4px 11px !important;

    &:focus,
    &:hover {
      border-color: #444CE7 !important;
    }
  }

  .ant-select-selection-placeholder {
    color: #bfbfbf !important;
    opacity: 1 !important;
    display: flex !important;
    align-items: center !important;
    height: 32px !important;
    line-height: 32px !important;
  }

  &.ant-select-disabled .ant-select-selection-placeholder {
    color: #d9d9d9 !important;
  }

  .ant-select-selection-search-input {
    height: 32px !important;
  }

  .ant-select-selection-item {
    display: flex !important;
    align-items: center !important;
    height: 32px !important;
    line-height: 32px !important;
  }
`

const StyledInput = styled(Input)`
  border-radius: 8px;
  border: 1px solid #e8e9ea;

  &:focus,
  &:hover {
    border-color: #444CE7;
  }
`

// Country code + national number sit side by side as one logical field: the dial
// code takes only the width its content needs, the number takes the rest.
// The min-width is the point of this component — it guarantees the number stays
// fully readable while typing. Without it the input is only whatever the row
// happens to leave over, which was ~62px (about four visible digits).
// FormRow sets `flex: 1` on both .ant-input and .ant-select at the same
// specificity, so `&&` (a doubled class) is needed to win independently of the
// order styled-components injects the two rules in.
const MobileFieldGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  && .ant-select {
    flex: 0 0 104px;
  }

  && .ant-input {
    flex: 1;
    min-width: 140px;
  }
`

// Row for a form's primary action. The button sizes to its label instead of
// stretching, so it stops competing with the inputs for width.
const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
`

// Global styles for hiding table scrollbars
const GlobalTableStyles = styled.div`
  .custom-scroll-container {
    &::-webkit-scrollbar {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }

  .hide-scrollbar-table {
    /* Hide scrollbars for all table components */
    .ant-table-container,
    .ant-table-body,
    .ant-table-content,
    .ant-table-tbody,
    .ant-table-scroll {
      &::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }
      
      &::-webkit-scrollbar-track {
        display: none !important;
      }
      
      &::-webkit-scrollbar-thumb {
        display: none !important;
      }
      
      &::-webkit-scrollbar-corner {
        display: none !important;
      }
      
      /* Hide scrollbar for Firefox */
      scrollbar-width: none !important;
      
      /* Hide scrollbar for IE and Edge */
      -ms-overflow-style: none !important;
    }
    
    /* Specific targeting for Ant Design table scroll containers */
    .ant-table-body {
      overflow: auto !important;
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;
      
      &::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
      }
    }
    
    .ant-table-content {
      overflow: visible !important;
    }
    
    /* Also target any div with scroll inside the table */
    & div[style*="overflow"] {
      &::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
      }
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;
    }
  }
`

const SCREENS = {
  MAIN_OPTIONS: 'main_options',
  SUMMARY: 'summary',
  UPLOAD_EXCEL: 'upload_excel',
  ADD_MANUALLY: 'add_manually',
}

const SOURCE_COLORS = {
  excel: '#f0f9ff',
  manual: '#fef3f2',
  existing: '#fefce8',
}

const SOURCE_LABELS = {
  excel: 'Excel Upload',
  manual: 'Manual Entry',
  existing: 'Existing List',
}

const safeRender = (value, fallback = '') => {
  if (value === null || value === undefined) {
    return fallback
  }
  if (typeof value === 'object') {
    // Handle different object structures
    return value.degreeName || value.streamName || value.specialisationName || value.name || String(value) || fallback
  }
  return String(value)
}

const EnhancedBulkUploadDrawer = ({
  visible,
  onClose,
  assessmentData,
  entityType = 'college',
  onDataSubmit,
  initialScreen = 'main_options', // Add this prop
  mode = 'create',
}) => {
  const [currentScreen, setCurrentScreen] = useState(SCREENS.MAIN_OPTIONS)
  const [candidates, setCandidates] = useState([])
  /* The end date is settled BEFORE anyone is added: while a notice is pending,
     the body below is blurred and inert and the footer action is disabled, so
     the admin cannot discover the deadline only after picking candidates.
     No notice (no end date, or the create flow) means no gate at all. */
  const [endDateResolved, setEndDateResolved] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState(null)
  const [filteredLists, setFilteredLists] = useState([])
  const [selectedLists, setSelectedLists] = useState([])
  const [searchQuery, setSearchQuery] = useState('')

  const [uploadedFile, setUploadedFile] = useState(null)
  const [excelData, setExcelData] = useState([])
  const [isDragging, setIsDragging] = useState(false)

  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [saveForm] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [loadingLists, setLoadingLists] = useState(false)

  const [existingLists, setExistingLists] = useState([])

  const [manualForm, setManualForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: DEFAULT_COUNTRY_CODE,
    phone: '',
    degree: '',
    department: '',
    specialization: '',
    passingYear: null,
  })
  const [formValidation, setFormValidation] = useState({
    degree: null,
    department: null,
    specialization: null,
    passingYear: null,
  })

  const [degrees, setDegrees] = useState([])
  const [departments, setDepartments] = useState([])
  const [specializations, setSpecializations] = useState([])
  const [selectedDegree, setSelectedDegree] = useState(null)
  const [selectedDepartment, setSelectedDepartment] = useState(null)
  const [selectedSpecialization, setSelectedSpecialization] = useState(null)

  // Broadcast state — driven by distributionType radio in parent form
  const isBroadcast = assessmentData?.isBroadcast || false
  const [broadcastStreams, setBroadcastStreams] = useState([])
  const [broadcastSpecs, setBroadcastSpecs] = useState([])
  const [broadcastScope, setBroadcastScope] = useState({ targetDegreeId: null, targetStreamId: null })
  const [broadcastLabels, setBroadcastLabels] = useState([])

  const fileInputRef = useRef(null)
  const [form] = Form.useForm()
  const getEntityText = () =>
    entityType === 'college' ? 'Students' : 'Candidates'

  // Saved candidate lists are scoped to the entity that owns them — the
  // institute for college assessments, the company for corporate ones. Same
  // convention role-based set groups use, so save and fetch agree on the key.
  // Callers pass the selected entity as `entityId`; the create flow still
  // sends it as `instituteId`.
  const listEntityType = entityType === 'corporate' ? 'corporate' : 'college'
  const listEntityId =
    assessmentData?.entityId || assessmentData?.instituteId || null

  const downloadTemplate = () => {
    try {
      // Create workbook with template headers
      const wb = XLSX.utils.book_new()

      // Define template headers (country_code + mobile_number are optional — used
      // for the SMS OTP). country_code defaults to +91 when blank, and
      // mobile_number is the national number without it.
      const headers = [
        'first_name',
        'last_name',
        'primary_email',
        'country_code',
        'mobile_number',
      ]

      // Create sample data for template
      const sampleData = [
        ['John', 'Doe', 'john.doe@example.com', '+91', '9876543210'],
        ['Jane', 'Smith', 'jane.smith@example.com', '', ''],
      ]

      // Combine headers with sample data
      const templateData = [headers, ...sampleData]

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(templateData)

      // Set column widths
      ws['!cols'] = [
        { width: 15 }, // first_name
        { width: 15 }, // last_name
        { width: 25 }, // primary_email
        { width: 14 }, // country_code (optional, defaults to +91)
        { width: 15 }, // mobile_number (optional)
      ]

      // Add the worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, `${getEntityText()} Template`)

      // Generate filename
      const filename = `${getEntityText().toLowerCase()}_upload_template.xlsx`

      // Download the file
      XLSX.writeFile(wb, filename)

      message.success('Template downloaded successfully!')
    } catch (error) {
      console.error('Error downloading template:', error)
      message.error('Failed to download template. Please try again.')
    }
  }

  const yearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let year = currentYear - 5; year <= currentYear + 10; year++) {
      years.push({ label: year.toString(), value: year })
    }
    return years
  }, [])

  const saveStudentListAPI = async listData => {
    try {

      const response = await adminRequest.post(
        'assessment/saveStudentList',
        listData
      )

      return response.data
    } catch (error) {
      console.error('Error saving student list:', error)
      throw error
    }
  }

  const getStudentListsAPI = async (entityId, searchQuery = '') => {

    try {

      const response = await adminRequest.get(
        'assessment/getStudentLists',
        {
          params: {
            entityType: listEntityType,
            entityId,
            searchQuery,
          },
        }
      )

      return response.data
    } catch (error) {
      console.error('Error fetching student lists:', error)
      throw error
    }
  }

  const loadExistingLists = async () => {
    if (!listEntityId) {
      setExistingLists([])
      return
    }

    try {
      setLoadingLists(true)
      const response = await getStudentListsAPI(listEntityId, searchQuery)


      let lists = []
      if (response?.data?.data && Array.isArray(response.data.data)) {
        lists = response.data.data
      } else if (response?.data && Array.isArray(response.data)) {
        lists = response.data
      }


      setExistingLists(lists)
      setFilteredLists(lists)
    } catch (error) {
      console.error('Error loading existing lists:', error)
      message.error('Failed to load existing lists')
      setExistingLists([])
      setFilteredLists([])
    } finally {
      setLoadingLists(false)
    }
  }

  useEffect(() => {
    if (visible && assessmentData?.instituteCampusId) {
      const fetchDegrees = async () => {
        try {
          console.log('[EnhancedBulkUploadDrawer] Fetching degrees with campusID:', assessmentData.instituteCampusId);

          const fetchFunction = getEligibilityCriteria({
            searchType: 'DEGREE',
            campusID: assessmentData.instituteCampusId
          });
          const result = await fetchFunction();
          console.log('[EnhancedBulkUploadDrawer] Degrees fetched:', result?.length || 0, 'items');
          setDegrees(result || []);

          if (assessmentData?.qualificationData && assessmentData.qualificationData.length > 0) {
            // Logic to pre-select based on existing data if needed
          }
        } catch (error) {
          console.error("[EnhancedBulkUploadDrawer] Error fetching degrees:", error);
        }
      };

      fetchDegrees();
      if (listEntityId) {
        loadExistingLists();
      }
    } else {
      console.warn('[EnhancedBulkUploadDrawer] No instituteCampusId available, cannot fetch degrees');
    }
  }, [visible, assessmentData?.instituteCampusId, listEntityId]);

  useEffect(() => {
    if (visible) {
      // Set the current screen based on the initialScreen prop
      setCurrentScreen(initialScreen === 'summary' ? SCREENS.SUMMARY : SCREENS.MAIN_OPTIONS)
      setSelectedMethod(null)

      setUploadedFile(null)
      setExcelData([])

      setManualForm({
        firstName: '',
        lastName: '',
        email: '',
        // Reset to the default dial code, not undefined — an undefined value
        // leaves the country-code Select rendered blank.
        countryCode: DEFAULT_COUNTRY_CODE,
        phone: '',
        degree: '',
        department: '',
        specialization: '',
        passingYear: null,
      })

      setSearchQuery('')
      form.resetFields()
      saveForm.resetFields()

      loadExistingLists()
    }
  }, [visible])

  useEffect(() => {
    if (
      visible &&
      assessmentData?.preservedCandidates &&
      assessmentData.preservedCandidates.length > 0
    ) {

      setCandidates(assessmentData.preservedCandidates)

      const firstCollegeCandidate = assessmentData.preservedCandidates.find(
        c => entityType === 'college' && c.degreeObj && c.departmentObj
      )

      if (firstCollegeCandidate && firstCollegeCandidate.degreeObj) {

        setSelectedDegree(firstCollegeCandidate.degreeObj)
        setFormValidation(prev => ({
          ...prev,
          degree: firstCollegeCandidate.degreeObj.degreeId,
        }))

        if (firstCollegeCandidate.departmentObj) {
          setDepartments(firstCollegeCandidate.degreeObj.streams || [])
          setSelectedDepartment(firstCollegeCandidate.departmentObj)
          setFormValidation(prev => ({
            ...prev,
            department: firstCollegeCandidate.departmentObj.streamId,
          }))
        }

        if (firstCollegeCandidate.specializationObj) {
          setSpecializations(
            firstCollegeCandidate.departmentObj?.specialisations || []
          )
          setSelectedSpecialization(firstCollegeCandidate.specializationObj)
          setFormValidation(prev => ({
            ...prev,
            specialization:
              firstCollegeCandidate.specializationObj.specialisationId,
          }))
        }

        if (firstCollegeCandidate.passingYear) {
          setFormValidation(prev => ({
            ...prev,
            passingYear: firstCollegeCandidate.passingYear,
          }))
        }
      }


      const listCandidates = assessmentData.preservedCandidates.filter(
        c => c.source === 'existing' && c.listId
      )
      if (listCandidates.length > 0) {
        const uniqueListIds = [...new Set(listCandidates.map(c => c.listId))]

        setTimeout(() => {
          if (existingLists.length > 0) {
            const restoredLists = existingLists.filter(list =>
              uniqueListIds.includes(list.id)
            )
            setSelectedLists(restoredLists)
          }
        }, 1000)
      } else {

        setSelectedLists([])
      }
    } else if (visible) {

      if (
        !assessmentData?.preservedCandidates ||
        assessmentData.preservedCandidates.length === 0
      ) {
        setCandidates([])
      }
    }
  }, [visible, assessmentData?.preservedCandidates, entityType])

  useEffect(() => {
    if (visible) {

      if (candidates.length > 0) {
        const sources = [...new Set(candidates.map(c => c.source || 'unknown'))]
      }
    }
  }, [candidates, visible])

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredLists(existingLists)
    } else {
      const filtered = existingLists.filter(
        list =>
          list.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          list.degree.toLowerCase().includes(searchQuery.toLowerCase()) ||
          list.department.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setFilteredLists(filtered)
    }
  }, [searchQuery, existingLists])

  const handleDegreeChange = async (value) => {
    const degree = degrees.find(d => d.degreeId === value)

    setManualForm(prev => ({
      ...prev,
      degree: value,
      department: null,
      specialization: null
    }));

    // Also update formValidation if it's used for display
    setFormValidation(prev => ({
      ...prev,
      degree: value,
      department: null,
      specialization: null,
    }));

    setSelectedDegree(degree);
    setDepartments([]);
    setSpecializations([]);
    setSelectedDepartment(null);
    setSelectedSpecialization(null);

    // Reset form fields
    form.setFieldsValue({ department: undefined, specialization: undefined })

    if (value) {
      // Use the streams from the selected degree object directly
      if (degree && degree.streams) {
        setDepartments(degree.streams);
      } else {
        setDepartments([]);
      }
    }
  };

  const handleDepartmentChange = (value) => {
    const department = departments.find(d => d.streamId === value)

    setManualForm(prev => ({
      ...prev,
      department: value,
      specialization: null
    }));

    setFormValidation(prev => ({
      ...prev,
      department: value,
      specialization: null,
    }));

    setSelectedDepartment(department);
    setSpecializations(department?.specialisations || []);
    setSelectedSpecialization(null);

    // Reset specialization field
    form.setFieldsValue({ specialization: undefined })
  };

  const handleSpecializationChange = (value) => {
    const specialization = specializations.find(s => s.specialisationId === value)
    setSelectedSpecialization(specialization)
    setManualForm(prev => ({ ...prev, specialization: value }));
    setFormValidation(prev => ({ ...prev, specialization: value }));
  };

  const handlePassingYearChange = (value) => {
    setManualForm(prev => ({ ...prev, passingYear: value }));
    setFormValidation(prev => ({ ...prev, passingYear: value }));
  };

  const handleSaveAsList = async () => {
    try {
      const values = await saveForm.validateFields()
      setSaving(true)

      if (!listEntityId) {
        message.error(
          `Cannot save the list — no ${listEntityType === 'corporate' ? 'company' : 'college'} selected`
        )
        return
      }

      const transformedStudentsData = candidates.map(candidate => ({
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        email: candidate.email,
        countryCode: candidate.countryCode || DEFAULT_COUNTRY_CODE,
        phone: candidate.phone || '',
        degree: candidate.degreeObj?.degreeName || candidate.degree || '',
        department:
          candidate.departmentObj?.streamName || candidate.department || '',
        specialization:
          candidate.specializationObj?.specialisationName ||
          candidate.specialization ||
          '',
        passingYear: candidate.passingYear || null,
        degreeObj: candidate.degreeObj,
        departmentObj: candidate.departmentObj,
        specializationObj: candidate.specializationObj,
      }))

      const uniqueStudentsData = transformedStudentsData.filter(
        (student, index, self) =>
          index ===
          self.findIndex(
            s => s.email.toLowerCase() === student.email.toLowerCase()
          )
      )

      const originalCount = transformedStudentsData.length
      const uniqueCount = uniqueStudentsData.length
      const duplicatesRemoved = originalCount - uniqueCount

      if (duplicatesRemoved > 0) {

        message.info(
          `Removed ${duplicatesRemoved} duplicate email(s). Saving ${uniqueCount} unique candidates.`
        )
      }
      const saveData = {
        entityType: listEntityType,
        entityId: listEntityId,
        listName: values.listName,
        studentsData: uniqueStudentsData,
        createdBy: '',
      }


      const result = await saveStudentListAPI(saveData)

      if (result.message) {
        message.success(`List "${values.listName}" saved successfully!`)
        setShowSaveDialog(false)
        saveForm.resetFields()

        await loadExistingLists()
      } else {
        message.error('Failed to save list')
      }
    } catch (error) {
      console.error('Error saving list:', error)
      if (error.response?.status === 409) {
        message.error(
          'A list with this name already exists. Please choose a different name.'
        )
      } else {
        message.error(error.response?.data?.message || 'Failed to save list')
      }
    } finally {
      setSaving(false)
    }
  }
  const isFormValid = () => {

    const totalCandidates =
      candidates.length ||
      (assessmentData?.preservedCandidates
        ? assessmentData.preservedCandidates.length
        : 0)
    const allCandidates =
      candidates.length > 0
        ? candidates
        : assessmentData?.preservedCandidates || []


    if (entityType !== 'college') {
      const isValid = totalCandidates > 0
      return isValid
    }


    const hasAcademicInfo =
      formValidation.degree &&
      formValidation.department &&
      formValidation.passingYear


    if (!hasAcademicInfo && totalCandidates > 0) {
      const firstCollegeCandidate = allCandidates.find(
        c => c.degreeObj && c.departmentObj && c.passingYear
      )
      const isValid = firstCollegeCandidate !== undefined && totalCandidates > 0

      return isValid
    }

    const isValid = hasAcademicInfo && totalCandidates > 0

    return isValid
  }

  const validateEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }
  const parseExcelFile = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      const excelId = `excel_${Date.now()}_${Math.random()}`

      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const jsonData = XLSX.utils.sheet_to_json(worksheet)
          const droppedPhoneEmails = new Set()

          const processedData = jsonData
            .map((row, index) => {
              const firstName =
                row['first_name'] ||
                row['First Name'] ||
                row['firstName'] ||
                row['FirstName'] ||
                row['FIRST_NAME'] ||
                row['First_Name'] ||
                row['fname'] ||
                row['Fname'] ||
                ''
              const lastName =
                row['last_name'] ||
                row['Last Name'] ||
                row['lastName'] ||
                row['LastName'] ||
                row['LAST_NAME'] ||
                row['Last_Name'] ||
                row['lname'] ||
                row['Lname'] ||
                ''
              const email =
                row['primary_email'] ||
                row['Email'] ||
                row['email'] ||
                row['Email Address'] ||
                row['email_address'] ||
                row['EMAIL'] ||
                row['PRIMARY_EMAIL'] ||
                row['Primary_Email'] ||
                row['emailAddress'] ||
                row['mail'] ||
                ''
              // Optional mobile number (SMS OTP) — many header spellings tolerated.
              const phone =
                row['mobile_number'] ||
                row['Mobile Number'] ||
                row['mobile'] ||
                row['Mobile'] ||
                row['phone'] ||
                row['Phone'] ||
                row['phone_number'] ||
                row['Phone Number'] ||
                row['contact_number'] ||
                row['Contact Number'] ||
                ''
              // Optional country code column. Sheets that omit it fall back to
              // +91, so every existing template keeps working unchanged.
              const countryCodeCell =
                row['country_code'] ||
                row['Country Code'] ||
                row['countryCode'] ||
                row['COUNTRY_CODE'] ||
                row['dial_code'] ||
                row['Dial Code'] ||
                ''

              // Ensure all values are strings
              const firstNameStr = String(firstName || '').trim()
              const lastNameStr = String(lastName || '').trim()
              const emailStr = String(email || '').trim()
              const phoneStr = String(phone || '').trim()

              if (
                !firstNameStr ||
                !emailStr ||
                !validateEmail(emailStr)
              ) {
                return null
              }

              // The number cell may itself carry a country code ("+971 50…"), so
              // split it and let the column act as the fallback.
              const fallbackCode = normalizeCountryCodeCell(countryCodeCell)
              const mobile = splitFullMobile(phoneStr, fallbackCode)
              if (phoneStr && !mobile) {
                droppedPhoneEmails.add(emailStr)
              }

              return {
                firstName: firstNameStr,
                lastName: lastNameStr,
                email: emailStr,
                countryCode: mobile?.countryCode || fallbackCode,
                phone: mobile?.nationalNumber || '',
                source: 'excel',
                excelFileName: file.name,
                excelId: excelId,
                id: Date.now() + Math.random() + index,
              }
            })
            .filter(Boolean)

          const uniqueData = processedData.filter(
            (item, index, self) =>
              index ===
              self.findIndex(
                t => t.email.toLowerCase() === item.email.toLowerCase()
              )
          )

          // An unusable mobile is dropped rather than failing the whole row (it is
          // optional), but say so — silently blanking it left recruiters believing
          // the SMS OTP was set up when the candidate got an email-only invite.
          const droppedPhones = uniqueData.filter(
            item => !item.phone && droppedPhoneEmails.has(item.email)
          ).length
          if (droppedPhones > 0) {
            message.warning(
              `${droppedPhones} mobile number(s) in ${file.name} are not valid for their country code and were skipped — those candidates will get an email-only OTP.`
            )
          }

          resolve(uniqueData)
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsArrayBuffer(file)
    })
  }

  const handleFileUpload = async file => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ]

    if (
      !validTypes.includes(file.type) &&
      !file.name.match(/\.(xlsx|xls|csv)$/i)
    ) {
      message.error('Please upload a valid Excel file (.xlsx, .xls, or .csv)')
      return false
    }

    if (file.size > 5 * 1024 * 1024) {
      message.error('File size must be less than 5MB')
      return false
    }

    try {
      const data = await parseExcelFile(file)

      if (data.length === 0) {
        message.error(
          'No valid data found in the file. Please check your column names and data format.'
        )
        return false
      }

      if (
        entityType === 'college' &&
        (!formValidation.degree ||
          !formValidation.department ||
          !formValidation.passingYear)
      ) {
        setExcelData(data)
        setUploadedFile(file)
        message.warning(
          'Please select degree, department, and passing year before uploading'
        )
        return false
      } const excelId = data.length > 0 ? data[0].excelId : `excel_${Date.now()}_${Math.random()}`
      const enhancedExcelData = data.map(record => ({
        ...record,
        excelFileName: file.name,
        excelId: excelId,
        ...(entityType === 'college' && {
          degree: selectedDegree?.degreeName || '',
          department: selectedDepartment?.streamName || '',
          specialization: selectedSpecialization?.specialisationName || '',
          passingYear: formValidation.passingYear,
          degreeObj: selectedDegree,
          departmentObj: selectedDepartment,
          specializationObj: selectedSpecialization,
        }),
      }))

      setCandidates(prev => [...prev, ...enhancedExcelData])
      setExcelData([])
      setUploadedFile(null)
      setCurrentScreen(SCREENS.SUMMARY)
      message.success(
        `${data.length} ${getEntityText().toLowerCase()} added from Excel`
      )
      return false
    } catch (error) {
      message.error(
        'Failed to parse Excel file. Please check the format and try again.'
      )
      return false
    }
  }

  const handleDrag = (e, isDragEnter) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(isDragEnter)
  }
  const handleDrop = async e => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      await handleFileUpload(files[0])
    }
  }

  const handleListSelection = list => {
    const isSelected = selectedLists.some(l => l.id === list.id)

    if (isSelected) {
      setSelectedLists(prev => prev.filter(l => l.id !== list.id))
      setCandidates(prev => prev.filter(c => c.listId !== list.id))
      message.success(`Removed "${list.name}" from selection`)
    } else {
      setSelectedLists(prev => [...prev, list])
      const listCandidates = list.candidates.map(c => ({
        ...c,
        source: 'existing',
        listId: list.id,
        listName: list.name,
        id: Date.now() + Math.random(),
        ...(entityType === 'college' &&
          c.degree && {
          degree: c.degree,
          department: c.department,
          specialization: c.specialization,
          passingYear: c.passingYear,
          degreeObj:
            typeof c.degree === 'object'
              ? c.degree
              : { degreeName: c.degree },
          departmentObj:
            typeof c.department === 'object'
              ? c.department
              : { streamName: c.department },
          specializationObj:
            typeof c.specialization === 'object'
              ? c.specialization
              : c.specialization
                ? { specialisationName: c.specialization }
                : null,
        }),
      }))
      setCandidates(prev => [...prev, ...listCandidates])
      message.success(
        `Added ${list.candidates.length
        } ${getEntityText().toLowerCase()} from "${list.name}"`
      )

      // Go directly to summary screen after adding list
      setCurrentScreen(SCREENS.SUMMARY)
    }
  }

  const renderMainOptions = () => (
    <ScreenContainer
      isVisible={currentScreen === SCREENS.MAIN_OPTIONS}
      offset={0}
    >
      <SectionTitle>
        <TeamOutlined />
        Add {getEntityText()}
      </SectionTitle>

      <MainOptionsGrid>
        <MethodCard
          selected={selectedMethod === 'upload'}
          onClick={() => {
            setSelectedMethod('upload')
            setCurrentScreen(SCREENS.UPLOAD_EXCEL)
            setSelectedMethod('excel')
          }}
        >
          <IconWrapper selected={selectedMethod === 'upload'}>
            <UploadOutlined />
          </IconWrapper>
          <MethodTitle selected={selectedMethod === 'upload'}>
            Upload via Excel
          </MethodTitle>
          <MethodDescription>
            Bulk upload {getEntityText().toLowerCase()} using Excel/CSV files
          </MethodDescription>
        </MethodCard>

        <MethodCard
          selected={selectedMethod === 'manual'}
          onClick={() => {
            setSelectedMethod('manual')
            setCurrentScreen(SCREENS.ADD_MANUALLY)
          }}
        >
          <IconWrapper selected={selectedMethod === 'manual'}>
            <PlusOutlined />
          </IconWrapper>
          <MethodTitle selected={selectedMethod === 'manual'}>
            Add Manually
          </MethodTitle>
          <MethodDescription>
            Add {getEntityText().toLowerCase()} one by one with manual entry
          </MethodDescription>
        </MethodCard>
      </MainOptionsGrid>

      <div style={{ marginTop: '32px' }}>
        <SubSectionTitle>
          <TeamOutlined />
          Select from Existing Lists
        </SubSectionTitle>

        {/* <StyledInput
          placeholder="Search existing lists..."
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={e => {
            setSearchQuery(e.target.value)
            // Trigger search on API if needed
          }}
          style={{ marginBottom: '20px' }}
        /> */}

        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {loadingLists ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div>Loading existing lists...</div>
            </div>
          ) : filteredLists.length === 0 ? (
            <EmptyState>
              <div>
                {existingLists.length === 0
                  ? 'No saved lists found. Create your first list by adding candidates and saving them.'
                  : 'No lists found matching your search'}
              </div>
            </EmptyState>
          ) : (
            filteredLists.map(list => {
              const isSelected = selectedLists.some(l => l.id === list.id)

              return (
                <ListCard
                  key={list.id}
                  selected={isSelected}
                  onClick={() => handleListSelection(list)}
                >
                  {isSelected && (
                    <UploadedBadge>
                      <CheckCircleOutlined />
                      Added
                    </UploadedBadge>
                  )}

                  <ListHeader>
                    <ListTitle>{safeRender(list.name)}</ListTitle>
                  </ListHeader>
                  <ListMeta>
                    {safeRender(list.degree)} • {safeRender(list.department)} • {safeRender(list.year)}
                  </ListMeta>

                  <ListFooter>
                    <div
                      style={{
                        fontSize: FONT.F_SM,
                        color: '#6b7280',
                        fontWeight: '500',
                      }}
                    >
                      {safeRender(list.candidates?.length, 0)} {getEntityText().toLowerCase()}
                    </div>
                    <Badge
                      count={list.candidates?.length || 0}
                      style={{
                        backgroundColor: isSelected ? '#52c41a' : '#444CE7',
                        fontSize: FONT.F_XS,
                      }}
                    />
                  </ListFooter>
                </ListCard>
              )
            })
          )}
        </div>
      </div>
    </ScreenContainer>
  )

  const renderUploadExcel = () => (
    <ScreenContainer
      isVisible={currentScreen === SCREENS.UPLOAD_EXCEL}
      offset={0}
    >
      <SectionTitle>
        <FileExcelOutlined />
        Upload via Excel
      </SectionTitle>

      {entityType === 'college' && (
        <Form form={form} layout="vertical">
          <RequiredFieldsSection>
            <div
              style={{
                fontWeight: '700',
                marginBottom: '16px',
                color: '#444CE7',
                fontSize: FONT.F_LG,
              }}
            >
              Academic Information Required
            </div>

            <FormRow>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                  }}
                >
                  Degree *
                </label>
                <StyledSelect
                  showSearch
                  allowClear
                  placeholder="Eg. Bachelor of Engineering - B.E"
                  options={degrees.map(degree => ({
                    label: degree.degreeName,
                    value: degree.degreeId,
                  }))}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleDegreeChange}
                  value={formValidation.degree || manualForm.degree}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                  }}
                >
                  Department *
                </label>
                <StyledSelect
                  showSearch
                  allowClear
                  placeholder="Eg. Computer Science"
                  options={departments.map(dept => ({
                    label: dept.streamName,
                    value: dept.streamId,
                  }))}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleDepartmentChange}
                  disabled={!selectedDegree}
                  value={formValidation.department || manualForm.department}

                />
              </div>
            </FormRow>

            <FormRow>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                  }}
                >
                  Specialization
                </label>
                <StyledSelect
                  showSearch
                  allowClear
                  placeholder="Select specialization (if any)"
                  options={specializations.map(spec => ({
                    label: spec.specialisationName,
                    value: spec.specialisationId,
                  }))}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleSpecializationChange}
                  disabled={!selectedDepartment}
                  value={formValidation.specialization}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                  }}
                >
                  Year of Passing *
                </label>
                <StyledSelect
                  showSearch
                  allowClear
                  placeholder="Select passing year"
                  options={yearOptions}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handlePassingYearChange}
                  value={formValidation.passingYear}
                />
              </div>
            </FormRow>
          </RequiredFieldsSection>
        </Form>
      )}      <InfoBanner>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <div
            style={{
              fontWeight: '700',
              color: '#444CE7',
              fontSize: FONT.F_MD,
            }}
          >
            Excel File Requirements:
          </div>

          <Button
            type="default"
            icon={<DownloadOutlined />}
            onClick={downloadTemplate}
            style={{
              borderRadius: '8px',
              fontWeight: '600',
              height: '36px',
              padding: '0 16px',
              border: '1px solid #444CE7',
              color: '#444CE7',
              backgroundColor: '#FFFFFF',
            }}
          >
            Download Template
          </Button>
        </div>

        <div
          style={{ fontSize: FONT.F_SM, color: '#666', marginBottom: '8px' }}
        >
          Your Excel file should contain these columns (case-insensitive):
        </div>
        <ul
          style={{
            margin: '8px 0',
            paddingLeft: '20px',
            fontSize: FONT.F_SM,
            color: '#374151',
          }}
        >
          <li>
            <strong>first_name</strong> or First Name or firstName
          </li>
          <li>
            <strong>last_name</strong> or Last Name or lastName
          </li>
          <li>
            <strong>primary_email</strong> or Email or email
          </li>
          <li>
            <strong>mobile_number</strong> or Mobile or Phone (optional — for SMS
            OTP). Just the national number, e.g. 9876543210
          </li>
          <li>
            <strong>country_code</strong> or Country Code (optional — defaults to
            +91). A number that already includes its own code, e.g. +971
            501234567, is read correctly either way
          </li>
        </ul>
      </InfoBanner>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".xlsx,.xls,.csv"
        onChange={e => {
          if (e.target.files[0]) {
            handleFileUpload(e.target.files[0])
          }
        }}
      />

      <UploadArea
        isDragging={isDragging}
        onClick={() => {
          if (
            entityType === 'college' &&
            (!formValidation.degree ||
              !formValidation.department ||
              !formValidation.passingYear)
          ) {
            message.warning(
              'Please select degree, department, and passing year first'
            )
            return
          }
          fileInputRef.current?.click()
        }}
        onDragEnter={e => handleDrag(e, true)}
        onDragLeave={e => handleDrag(e, false)}
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
      >
        <UploadOutlined style={{ fontSize: '48px', color: '#444CE7' }} />
        <div
          style={{ fontSize: FONT.F_XL, fontWeight: '700', color: '#444CE7' }}
        >
          {isDragging
            ? 'Drop your file here'
            : 'Browse for file or Drag and drop'}
        </div>
        <div style={{ color: '#666', fontSize: FONT.F_MD, fontWeight: '500' }}>
          Supports: .xlsx, .xls, .csv (Max 5MB)
        </div>
      </UploadArea>
    </ScreenContainer>
  )

  const renderAddManually = () => (
    <ScreenContainer
      isVisible={currentScreen === SCREENS.ADD_MANUALLY}
      offset={0}
    >
      <SectionTitle>
        <PlusOutlined />
        Add Candidates Manually
      </SectionTitle>

      {entityType === 'college' && (
        <RequiredFieldsSection>
          <div
            style={{
              fontWeight: '700',
              marginBottom: '16px',
              color: '#444CE7',
              fontSize: FONT.F_LG,
            }}
          >
            Academic Information (Required for all candidates)
          </div>

          <FormRow>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                }}
              >
                Degree *
              </label>
              <StyledSelect
                showSearch
                allowClear
                placeholder="Select Degree"
                options={degrees.map(degree => ({
                  label:
                    degree.degreeName +
                    (degree.degreeShortForm
                      ? ` - ${degree.degreeShortForm}`
                      : ''),
                  value: degree.degreeId,
                }))}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleDegreeChange}
                value={formValidation.degree}
              />
            </div>

            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                }}
              >
                Department *
              </label>
              <StyledSelect
                showSearch
                allowClear
                placeholder="Select Department"
                options={departments.map(dept => ({
                  label: dept.streamName,
                  value: dept.streamId,
                }))}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleDepartmentChange}
                disabled={!selectedDegree}
                value={formValidation.department}
              />
            </div>
          </FormRow>

          <FormRow>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                }}
              >
                Specialization
              </label>
              <StyledSelect
                showSearch
                allowClear
                placeholder="Select Specialization (Optional)"
                options={specializations.map(spec => ({
                  label: spec.specialisationName,
                  value: spec.specialisationId,
                }))}
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleSpecializationChange}
                disabled={!selectedDepartment}
                value={formValidation.specialization}
              />
            </div>

            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                }}
              >
                Passing Year *
              </label>
              <StyledSelect
                showSearch
                allowClear
                placeholder="Select Passing Year"
                options={yearOptions}
                onChange={handlePassingYearChange}
                value={formValidation.passingYear}
              />
            </div>
          </FormRow>
        </RequiredFieldsSection>
      )}

      <ManualEntryCard>
        <SubSectionTitle>
          Add New {entityType === 'college' ? 'Candidate' : 'Candidate'}
        </SubSectionTitle>

        <FormRow>
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
              }}
            >
              First Name *
            </label>
            <StyledInput
              placeholder="Enter first name"
              value={manualForm.firstName}
              onChange={e =>
                setManualForm({ ...manualForm, firstName: e.target.value })
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
              }}
            >
              Last Name
            </label>
            <StyledInput
              placeholder="Enter last name"
              value={manualForm.lastName}
              onChange={e =>
                setManualForm({ ...manualForm, lastName: e.target.value })
              }
            />
          </div>
        </FormRow>

        <FormRow>
          <div style={{ flex: 2 }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
              }}
            >
              Email Address *
            </label>
            <StyledInput
              placeholder="Enter email address"
              type="email"
              value={manualForm.email}
              onChange={e =>
                setManualForm({ ...manualForm, email: e.target.value })
              }
            />
          </div>
          <div style={{ flex: 2 }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
              }}
            >
              Mobile Number
            </label>
            {/* Country code is a separate field so the number box is unambiguously
                the national number — recruiters no longer have to guess whether
                the +91 belongs inside it. */}
            <MobileFieldGroup>
              <StyledSelect
                showSearch
                aria-label="Country dialling code"
                value={manualForm.countryCode || DEFAULT_COUNTRY_CODE}
                options={countryCodeOptions}
                optionFilterProp="searchText"
                onChange={value =>
                  setManualForm({ ...manualForm, countryCode: value })
                }
              />
              <StyledInput
                placeholder={`Optional — ${nationalNumberLength(
                  manualForm.countryCode
                )} digits`}
                type="tel"
                aria-label="Mobile number without country code"
                maxLength={16}
                value={manualForm.phone}
                onChange={e =>
                  setManualForm({
                    ...manualForm,
                    phone: e.target.value.replace(/[^\d+\-\s]/g, ''),
                  })
                }
              />
            </MobileFieldGroup>
          </div>
        </FormRow>

        <FormActions>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={addManualCandidate}
            disabled={
              !manualForm.firstName.trim() ||
              !manualForm.email.trim() ||
              (entityType === 'college' &&
                (!formValidation.degree ||
                  !formValidation.department ||
                  !formValidation.passingYear))
            }
            style={{
              height: '40px',
              borderRadius: '8px',
              fontWeight: '600',
            }}
          >
            Add Candidate
          </Button>
        </FormActions>
      </ManualEntryCard>

      {candidates.filter(c => c.source === 'manual').length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <SubSectionTitle>
            Added {getEntityText()} (
            {candidates.filter(c => c.source === 'manual').length})
          </SubSectionTitle>

          <div
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              padding: '12px',
            }}
          >
            {' '}
            {candidates
              .filter(c => c.source === 'manual')
              .map(candidate => (
                <CandidateItem
                  key={candidate.id}
                  sourceColor={SOURCE_COLORS.manual}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: '600',
                        fontSize: FONT.F_MD,
                        color: '#1f2937',
                      }}
                    >
                      {safeRender(candidate.firstName)} {safeRender(candidate.lastName)}
                    </div>
                    <div
                      style={{
                        fontSize: FONT.F_SM,
                        color: '#6b7280',
                        marginTop: '4px',
                      }}
                    >
                      {safeRender(candidate.email)}
                      {candidate.phone && (
                        <div
                          style={{
                            fontSize: FONT.F_XS,
                            color: '#9ca3af',
                            marginTop: '2px',
                          }}
                        >
                          📱{' '}
                          {safeRender(
                            formatMobile(candidate.countryCode, candidate.phone)
                          )}
                        </div>
                      )}
                      {entityType === 'college' &&
                        (candidate.degree?.degreeName ||
                          candidate.degreeObj?.degreeName ||
                          candidate.degree) && (
                          <div
                            style={{
                              fontSize: FONT.F_XS,
                              color: '#9ca3af',
                              marginTop: '2px',
                            }}
                          >
                            {safeRender(candidate.degreeObj?.degreeName) ||
                              safeRender(candidate.degree?.degreeName) ||
                              safeRender(candidate.degree)}{' '}
                            •{' '}
                            {safeRender(candidate.departmentObj?.streamName) ||
                              safeRender(candidate.department?.streamName) ||
                              safeRender(candidate.department)}{' '}
                            • {safeRender(candidate.passingYear)}
                            {(candidate.specializationObj?.specialisationName ||
                              candidate.specialization?.specialisationName ||
                              candidate.specialization) &&
                              ` • ${safeRender(
                                candidate.specializationObj?.specialisationName
                              ) ||
                              safeRender(candidate.specialization?.specialisationName) ||
                              safeRender(candidate.specialization)}`}
                          </div>
                        )}
                    </div>
                  </div>
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => removeCandidate(candidate.id)}
                    style={{ borderRadius: '6px' }}
                  >
                  </Button>
                </CandidateItem>
              ))}

          </div>
        </div>
      )}    </ScreenContainer>
  )

  const renderSummary = () => {


    // Group candidates by individual sources (Excel files, Lists, Manual)
    const individualSources = candidates.reduce((acc, candidate) => {
      let sourceKey = ''
      let sourceName = ''
      let sourceColor = ''

      if (candidate.source === 'excel') {
        sourceKey = candidate.excelId || `excel_${candidate.excelFileName}`
        sourceName = candidate.excelFileName || 'Excel Upload'
        sourceColor = '#f0f9ff'
      } else if (candidate.source === 'existing') {
        sourceKey = `list_${candidate.listId}`
        sourceName = candidate.listName || 'Existing List'
        sourceColor = '#fefce8'
      } else if (candidate.source === 'manual') {
        sourceKey = 'manual_entry'
        sourceName = 'Manual Entry'
        sourceColor = '#fef3f2'
      }

      if (!acc[sourceKey]) {
        acc[sourceKey] = {
          candidates: [],
          name: sourceName,
          color: sourceColor,
          type: candidate.source,
          ...(candidate.source === 'excel' && { excelId: candidate.excelId, fileName: candidate.excelFileName }),
          ...(candidate.source === 'existing' && { listId: candidate.listId }),
        }
      }

      acc[sourceKey].candidates.push(candidate)
      return acc
    }, {})

    return (
      <ScreenContainer isVisible={currentScreen === SCREENS.SUMMARY} offset={0}>
        <div style={{ marginBottom: '24px' }}>
          <SectionTitle>
            <TeamOutlined />
            {getEntityText()} Summary
          </SectionTitle>          {/* Summary Statistics */}
          <div
            style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}
          >            {/* Total Count and Save Button - Compact Header */}
            <div
              style={{
                width: '100%',
                padding: '16px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #000000',
                marginBottom: '16px',

              }}
            >
              {(candidates.length > 0 || (assessmentData?.preservedCandidates && assessmentData.preservedCandidates.length > 0)) && (
                <div style={{ fontSize: FONT.F_LG, fontWeight: '400', color: '#444ce7' }}>
                  {/* {candidates.length || (assessmentData?.preservedCandidates ? assessmentData.preservedCandidates.length : 0)} Candidates total */}

                  Do you wish to save candidates as new list? you can reuse for future in assessments
                </div>
              )}

              {/* Save as List Button */}
              {(candidates.length > 0 || (assessmentData?.preservedCandidates && assessmentData.preservedCandidates.length > 0)) && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowSaveDialog(true)}
                  style={{
                    borderRadius: '6px',
                    fontWeight: '500',
                    height: '36px',
                    padding: '0 16px',
                    fontSize: FONT.F_SM,
                    background: '#444CE7',
                    border: 'none'
                  }}
                >
                  Save as List
                </Button>
              )}
            </div>          </div>

          {/* Total Candidates Badge + Source Breakdown */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '24px',
              flexWrap: 'wrap'
            }}
          >
            {/* Total Candidates Badge */}
            <div
              style={{
                padding: '16px 20px',
                background: '#ffffff',
                borderRadius: '8px',
                border: '2px solid #444CE7',
                textAlign: 'center',
                minWidth: '180px',
                maxWidth: '220px'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: '#444CE7'
                  }}
                />
                <span
                  style={{
                    fontSize: FONT.F_SM,
                    color: '#444CE7',
                    display: 'inline-block'
                  }}
                >
                  Total
                </span>
              </div>
              <div style={{ fontSize: FONT.F_XL, fontWeight: '600', color: '#444CE7' }}>
                {candidates.length || (assessmentData?.preservedCandidates ? assessmentData.preservedCandidates.length : 0)}
              </div>
              <div style={{ fontSize: FONT.F_SM, color: '#444CE7', fontWeight: '500', marginTop: '14px' }}>
                Candidates
              </div>
            </div>

            {/* Source Breakdown Badges */}
            {Object.keys(individualSources).length > 0 && Object.entries(individualSources).map(([sourceKey, sourceData]) => (<div
              key={sourceKey}
              style={{
                padding: '16px 20px',
                background: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                textAlign: 'center',
                minWidth: '180px',
                maxWidth: '220px'
              }}
            ><div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: sourceData.color
                  }}
                />                    <span
                  style={{
                    fontSize: FONT.F_SM,
                    color: '#6b7280',
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block'
                  }}
                  title={sourceData.name}
                >
                  {sourceData.name}
                </span>
              </div>
              <div style={{ fontSize: FONT.F_XL, fontWeight: '600', color: '#1f2937' }}>
                {sourceData.candidates.length}
              </div>
              <Button
                size="small"
                danger
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => removeIndividualSource(sourceKey, sourceData)}
                style={{
                  marginTop: '8px',
                  fontSize: FONT.F_XS,
                  height: '28px',
                  padding: '0 8px'
                }}
              >
                Remove                  </Button>
            </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        {candidates.length === 0 &&
          (!assessmentData?.preservedCandidates ||
            assessmentData.preservedCandidates.length === 0) ? (
          <EmptyState>
            <div
              style={{
                fontSize: FONT.F_XL,
                marginBottom: '12px',
                fontWeight: '600',
              }}
            >
              No {getEntityText().toLowerCase()} added yet
            </div>
            <div style={{ fontSize: FONT.F_MD }}>
              Please add {getEntityText().toLowerCase()} to continue with the
              assessment creation.
            </div>
          </EmptyState>
        ) : (<div
          style={{
            background: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <div
            style={{
              maxHeight: '400px',
              overflow: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
            className="custom-scroll-container"
          ><Table
              columns={[
                {
                  title: 'Name',
                  key: 'fullName',
                  width: '220px',
                  render: (_, record) => (
                    <div>
                      <div style={{ fontWeight: '600', color: '#1f2937' }}>
                        {safeRender(record.firstName)} {safeRender(record.lastName)}
                      </div>
                      <div style={{ fontSize: FONT.F_SM, color: '#6b7280' }}>
                        {safeRender(record.email)}
                      </div>
                    </div>
                  )
                },
                {
                  title: 'Mobile',
                  dataIndex: 'phone',
                  key: 'phone',
                  width: '160px',
                  render: (phone, record) => (
                    <span style={{ fontSize: FONT.F_SM, color: phone ? '#1f2937' : '#9ca3af' }}>
                      {phone
                        ? safeRender(formatMobile(record.countryCode, phone))
                        : '—'}
                    </span>
                  )
                },
                {
                  title: 'Source',
                  dataIndex: 'source',
                  key: 'source',
                  width: '180px',
                  render: (source, record) => {
                    let sourceName = ''
                    let sourceColor = ''

                    if (source === 'excel') {
                      sourceName = safeRender(record.excelFileName, 'Excel Upload')
                      sourceColor = '#f0f9ff'
                    } else if (source === 'existing') {
                      sourceName = safeRender(record.listName, 'Existing List')
                      sourceColor = '#fefce8'
                    } else if (source === 'manual') {
                      sourceName = 'Manual Entry'
                      sourceColor = '#fef3f2'
                    }

                    return (
                      <Tag
                        style={{
                          backgroundColor: sourceColor,
                          color: source === 'excel' ? '#1e40af' : source === 'manual' ? '#dc2626' : '#ca8a04',
                          border: `1px solid ${source === 'excel' ? '#bfdbfe' : source === 'manual' ? '#fecaca' : '#fef3c7'}`,
                          borderRadius: '6px',
                          padding: '4px 8px',
                          fontWeight: '400',
                          fontSize: '11px',
                          lineHeight: '14px',
                          maxWidth: '160px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'inline-block'
                        }}
                        title={sourceName}
                      >
                        {sourceName}
                      </Tag>
                    )
                  },
                  filters: Object.entries(individualSources).map(([sourceKey, sourceData]) => ({
                    text: safeRender(sourceData.name),
                    value: sourceKey
                  })),
                  onFilter: (value, record) => {
                    if (record.source === 'excel') {
                      return (record.excelId || `excel_${record.excelFileName}`) === value
                    } else if (record.source === 'existing') {
                      return `list_${record.listId}` === value
                    } else if (record.source === 'manual') {
                      return value === 'manual_entry'
                    }
                    return false
                  }
                },
                ...(entityType === 'college' ? [
                  {
                    title: 'Academic Details',
                    key: 'academicInfo',
                    width: '250px',
                    render: (_, record) => {
                      // Safe extraction with proper string conversion
                      const degree = safeRender(record.degreeObj?.degreeName) ||
                        safeRender(record.degree) ||
                        ''

                      const department = safeRender(record.departmentObj?.streamName) ||
                        safeRender(record.department) ||
                        ''

                      const specialization = safeRender(record.specializationObj?.specialisationName) ||
                        safeRender(record.specialization) ||
                        ''

                      const passingYear = safeRender(record.passingYear) || ''

                      if (!degree) return <span>-</span>

                      return (
                        <div style={{ fontSize: FONT.F_SM }}>
                          <div style={{ fontWeight: '500', color: '#374151' }}>
                            {degree}
                          </div>
                          <div style={{ color: '#6b7280' }}>
                            {department}
                            {specialization && ` • ${specialization}`}
                            {passingYear && ` • ${passingYear}`}
                          </div>
                        </div>
                      )
                    }
                  }
                ] : []),

                {
                  title: 'Actions',
                  key: 'actions',
                  width: '80px',
                  render: (_, record) => (
                    <Button
                      size="small"
                      danger
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => removeCandidate(record.id)}
                      style={{
                        borderRadius: '6px',
                        color: '#ff4d4f',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Remove candidate"
                    />
                  )
                }
              ]}
              dataSource={candidates.map(candidate => ({
                ...candidate,
                key: candidate.id
              }))}
              pagination={false}
              scroll={{
                y: 400,
                scrollToFirstRowOnChange: true
              }} size="middle"
              className="hide-scrollbar-table"
            />
          </div>
        </div>
        )}

        {/* Save as List Dialog */}
        <Modal
          title="Save as List"
          open={showSaveDialog}
          onOk={handleSaveAsList}
          onCancel={() => {
            setShowSaveDialog(false)
            saveForm.resetFields()
          }}
          confirmLoading={saving}
          okText="Save List"
          cancelText="Cancel"
          width={400}
        >
          <Form form={saveForm} layout="vertical" style={{ marginTop: '20px' }}>
            <Form.Item
              name="listName"
              label="List Name"
              rules={[
                { required: true, message: 'Please enter a list name' },
                { min: 3, message: 'List name must be at least 3 characters' },
                {
                  max: 100,
                  message: 'List name must be less than 100 characters',
                },
              ]}
            >
              <Input
                placeholder="Enter a unique name for this list"
                maxLength={100}
              />
            </Form.Item>

            <div
              style={{
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666',
              }}
            >
              This list will contain{' '}
              {candidates.length} candidates
              {' '}and will be saved for future use in assessments.
            </div>
          </Form>
        </Modal>
      </ScreenContainer>
    )
  }

  // Effect to hide scrollbars after component mounts
  useEffect(() => {
    const hideScrollbars = () => {
      const style = document.createElement('style')
      style.textContent = `
        .hide-scrollbar-table .ant-table-body::-webkit-scrollbar,
        .hide-scrollbar-table .ant-table-container::-webkit-scrollbar,
        .hide-scrollbar-table .ant-table-content::-webkit-scrollbar,
        .hide-scrollbar-table .ant-table-scroll::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
        .hide-scrollbar-table .ant-table-body,
        .hide-scrollbar-table .ant-table-container,
        .hide-scrollbar-table .ant-table-content,
        .hide-scrollbar-table .ant-table-scroll {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
      `
      document.head.appendChild(style)
      return () => document.head.removeChild(style)
    }

    const cleanup = hideScrollbars()
    return cleanup
  }, [visible])

  const addManualCandidate = () => {
    if (
      !manualForm.firstName.trim() ||
      !manualForm.email.trim()
    ) {
      message.error('Please fill in all required fields')
      return
    }

    if (
      entityType === 'college' &&
      (!formValidation.degree ||
        !formValidation.department ||
        !formValidation.passingYear)
    ) {
      message.error('Please select degree, department, and passing year')
      return
    }

    if (!validateEmail(manualForm.email)) {
      message.error('Please enter a valid email address')
      return
    }

    // Mobile number is optional; validate only when the recruiter enters one.
    const enteredPhone = manualForm.phone ? manualForm.phone.trim() : ''
    const mobile = parseCandidateMobile(manualForm.countryCode, enteredPhone)
    if (enteredPhone && !mobile) {
      message.error(
        `Please enter a valid ${nationalNumberLength(
          manualForm.countryCode
        )}-digit mobile number for ${manualForm.countryCode} (without the country code)`
      )
      return
    }

    const emailExists = candidates.some(
      c => c.email.toLowerCase() === manualForm.email.trim().toLowerCase()
    )

    if (emailExists) {
      message.error('This email address is already added')
      return
    }

    const newCandidate = {
      firstName: manualForm.firstName.trim(),
      lastName: manualForm.lastName ? manualForm.lastName.trim() : '',
      email: manualForm.email.trim(),
      countryCode: mobile?.countryCode || manualForm.countryCode,
      phone: mobile?.nationalNumber || '',
      source: 'manual',
      id: Date.now() + Math.random(),
      ...(entityType === 'college' && {
        // Store both string values and objects for compatibility
        degree: selectedDegree?.degreeName || '',
        department: selectedDepartment?.streamName || '',
        specialization: selectedSpecialization?.specialisationName || '',
        passingYear: formValidation.passingYear,
        degreeObj: selectedDegree,
        departmentObj: selectedDepartment,
        specializationObj: selectedSpecialization,
      }),
    }

    setCandidates(prev => [...prev, newCandidate])
    setManualForm({
      firstName: '',
      lastName: '',
      email: '',
      // Keep the chosen country code — recruiters add candidates in batches from
      // the same region, so resetting it to +91 every time would be a nuisance.
      countryCode: manualForm.countryCode,
      phone: '',
      degree: '',
      department: '',
      specialization: '',
      passingYear: null,
    })
    message.success(
      `${entityType === 'college' ? 'Student' : 'Candidate'} added successfully`
    )
  }
  const removeCandidate = id => {
    setCandidates(prev => prev.filter(c => c.id !== id))
    message.success('Removed successfully')
  }

  const removeAllCandidatesBySource = source => {
    const candidatesToRemove = candidates.filter(c => c.source === source)
    if (candidatesToRemove.length === 0) {
      message.info('No candidates to remove')
      return
    }

    setCandidates(prev => prev.filter(c => c.source !== source))
    message.success(
      `Removed all ${candidatesToRemove.length} ${getEntityText().toLowerCase()} from ${SOURCE_LABELS[source]}`
    )
  }

  // Remove candidates from a specific individual source
  const removeIndividualSource = (sourceKey, sourceData) => {
    let candidatesToRemove = []

    if (sourceData.type === 'excel') {
      candidatesToRemove = candidates.filter(c =>
        c.source === 'excel' &&
        (c.excelId === sourceData.excelId || c.excelFileName === sourceData.fileName)
      )
    } else if (sourceData.type === 'existing') {
      candidatesToRemove = candidates.filter(c =>
        c.source === 'existing' && c.listId === sourceData.listId
      )
    } else if (sourceData.type === 'manual') {
      candidatesToRemove = candidates.filter(c => c.source === 'manual')
    }

    if (candidatesToRemove.length === 0) {
      message.info('No candidates to remove')
      return
    }

    // Remove the candidates
    if (sourceData.type === 'excel') {
      setCandidates(prev => prev.filter(c =>
        !(c.source === 'excel' &&
          (c.excelId === sourceData.excelId || c.excelFileName === sourceData.fileName))
      ))
    } else if (sourceData.type === 'existing') {
      setCandidates(prev => prev.filter(c =>
        !(c.source === 'existing' && c.listId === sourceData.listId)
      ))
      // Also remove from selected lists
      setSelectedLists(prev => prev.filter(l => l.id !== sourceData.listId))
    } else if (sourceData.type === 'manual') {
      setCandidates(prev => prev.filter(c => c.source !== 'manual'))
    }

    message.success(
      `Removed ${candidatesToRemove.length} ${getEntityText().toLowerCase()} from "${sourceData.name}"`
    )
  }

  const getBackScreen = () => {
    switch (currentScreen) {
      case SCREENS.SUMMARY:
      case SCREENS.UPLOAD_EXCEL:
      case SCREENS.ADD_MANUALLY:
        return SCREENS.MAIN_OPTIONS
      default:
        return null
    }
  }

  const getNextAction = () => {
    switch (currentScreen) {
      case SCREENS.SUMMARY:
        return {
          text: 'Done',
          action: () => {
            if (!isFormValid()) {
              if (entityType === 'college') {
                message.error(
                  'Please select degree, department, passing year and add at least one student'
                )
              } else {
                message.error('Please add at least one candidate')
              }
              return
            }

            const transformedCandidates = candidates.map(candidate => ({
              ...candidate,
              ...(entityType === 'college' && {
                degree: candidate.degreeObj || selectedDegree,
                degreeId: (candidate.degreeObj || selectedDegree)?.degreeId,
                department: candidate.departmentObj || selectedDepartment,
                departmentId: (candidate.departmentObj || selectedDepartment)?.streamId,
                specialization:
                  candidate.specializationObj || selectedSpecialization,
                passingYear:
                  candidate.passingYear || formValidation.passingYear,
              }),
            }))

            const finalCandidates = transformedCandidates.filter(
              (candidate, index, self) =>
                index ===
                self.findIndex(
                  c => c.email.toLowerCase() === candidate.email.toLowerCase()
                )
            )

            const originalCount = transformedCandidates.length
            const uniqueCount = finalCandidates.length
            const duplicatesRemoved = originalCount - uniqueCount

            if (duplicatesRemoved > 0) {

              message.info(
                `Removed ${duplicatesRemoved} duplicate email(s). Submitting ${uniqueCount} unique ${getEntityText().toLowerCase()}.`
              )
            }

            onDataSubmit(finalCandidates)
            onClose()
            message.success(
              `${finalCandidates.length
              } ${getEntityText().toLowerCase()} submitted successfully`
            )
          },
          disabled: !isFormValid(),
        }
      default:
        return null
    }
  }
  const endDateNotice =
    mode === 'addCandidate'
      ? buildEndDateNotice(
          assessmentData?.assessmentEndDate,
          new Date(),
          assessmentData?.assessmentStartDate
        )
      : null
  const endDateGated = Boolean(endDateNotice) && !endDateResolved

  const nextAction = getNextAction()
  const backScreen = getBackScreen()
  const handleDrawerClose = () => {


    if (candidates.length > 0) {


      const finalCandidates = candidates.map(candidate => ({
        ...candidate,
        ...(entityType === 'college' && {
          degree: candidate.degreeObj || selectedDegree,
          department: candidate.departmentObj || selectedDepartment,
          specialization: candidate.specializationObj || selectedSpecialization,
          passingYear: candidate.passingYear || formValidation.passingYear,
        }),
      }))

      onDataSubmit(finalCandidates, { silent: true }) // Pass a silent flag if needed
    }

    onClose()
  }


  return (
    <GlobalTableStyles>
      <Drawer
        title={mode === 'addCandidate' ? 'Add Candidate' : undefined}
        placement="right"
        open={visible}
        onClose={handleDrawerClose}
        width={850}
        bodyStyle={{ padding: 0, height: '100%' }}
        drawerStyle={{ paddingBottom: 0 }}
        content={
          <DrawerContainer>
            {/* Only when adding to an assessment that already exists — the
                create flow has no end date yet, and the caller passes none. */}
            {mode === 'addCandidate' && (
              <AssessmentEndDateBanner
                endDate={assessmentData?.assessmentEndDate}
                startDate={assessmentData?.assessmentStartDate}
                assessmentInstituteMapId={assessmentData?.assessmentInstituteMapId}
                assessmentCorporateMapId={assessmentData?.assessmentCorporateMapId}
                onEndDateUpdated={assessmentData?.onEndDateUpdated}
                resolved={endDateResolved}
                onResolvedChange={setEndDateResolved}
              />
            )}
            <ContentWrapper
              data-gated={endDateGated ? 'true' : 'false'}
              aria-hidden={endDateGated || undefined}
              // React 18 forwards a string-valued `inert`; a boolean would be
              // dropped as an unknown attribute.
              {...(endDateGated ? { inert: '' } : {})}
            >
              {renderMainOptions()}
              {renderUploadExcel()}
              {renderAddManually()}
              {renderSummary()}
            </ContentWrapper>

            <FooterButtons>
              <div>
                {backScreen && (
                  <Button
                    size="large"
                    onClick={() => setCurrentScreen(backScreen)}
                    style={{ borderRadius: '8px' }}
                  >
                    Back
                  </Button>
                )}
              </div>{' '}
              <div style={{ display: 'flex', gap: '12px' }}>

                {(candidates.length > 0 ||
                  (assessmentData?.preservedCandidates &&
                    assessmentData.preservedCandidates.length > 0)) &&
                  currentScreen !== SCREENS.SUMMARY && (
                    <Button
                      size="large"
                      icon={<TeamOutlined />}
                      onClick={() => setCurrentScreen(SCREENS.SUMMARY)}
                      style={{ borderRadius: '8px' }}
                    >
                      View Summary (
                      {candidates.length ||
                        (assessmentData?.preservedCandidates
                          ? assessmentData.preservedCandidates.length
                          : 0)}
                      )
                    </Button>
                  )}              {currentScreen === SCREENS.SUMMARY && (
                    <Button
                      size="large"
                      icon={<PlusOutlined />}
                      onClick={() => setCurrentScreen(SCREENS.MAIN_OPTIONS)}
                      style={{
                        borderRadius: '8px',
                        backgroundColor: '#2E8B57',
                        borderColor: '#52c41a',
                        color: 'white'
                      }}
                    >
                      Add More Candidates
                    </Button>
                  )}

                {nextAction && (
                  <Button
                    type="primary"
                    size="large"
                    onClick={nextAction.action}
                    disabled={nextAction.disabled || endDateGated}
                    style={{
                      borderRadius: '8px',
                      fontWeight: '600',
                      minWidth: '120px',
                    }}
                  >
                    {nextAction.text}
                  </Button>
                )}
              </div>          </FooterButtons>
          </DrawerContainer>
        }
      />
    </GlobalTableStyles>
  )
}

export default EnhancedBulkUploadDrawer
